import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { IconCerrar, IconCheck, IconDespliega, IconOpciones } from './iconos';
import { etiquetaDelDia, letraDelDia } from './tira';

/* Piezas comunes. Ninguna decide colores ni tamaños: todo sale de `app.css`, que
   a su vez solo usa tokens. Aquí vive el comportamiento, no el aspecto. */

/**
 * LA PISTA DE MUESCAS, en un solo sitio.
 *
 * Veinticinco, y el paso se deriva de la vuelta en vez de fijarse en píxeles.
 * Con un patrón en píxeles —«guion de 8, hueco de 12»— la circunferencia casi
 * nunca es múltiplo del paso y el último guion se corta a la mitad, justo
 * arriba, que es por donde el ojo empieza a leer el anillo. Derivándolo, el
 * anillo cierra a cualquier tamaño.
 */
const MUESCAS = 25;
const RADIO = 80;
const VUELTA = 2 * Math.PI * RADIO;

export function Card({
  children,
  title,
  action,
  quiet = false,
  className = '',
}: {
  children: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  quiet?: boolean;
  className?: string;
}): ReactElement {
  return (
    <section className={`card${quiet ? ' card--quiet' : ''} ${className}`.trim()}>
      {title !== undefined || action !== undefined ? (
        <header className="section-head">
          {typeof title === 'string' ? <h2 className="t-title-3">{title}</h2> : title}
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/**
 * El anillo del nivel. `stroke-dasharray` sobre un círculo: sin librería de
 * gráficos, sin canvas, y se anima solo con `stroke-dashoffset`, que es una
 * propiedad compuesta y no cuesta fotogramas.
 */
export function Ring({
  progress,
  value,
  label,
  size = 96,
}: {
  progress: number;
  value: ReactNode;
  label?: string;
  size?: number;
}): ReactElement {
  const radius = size / 2 - 6;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, progress));
  // La misma pista de muescas que la métrica radial: un anillo liso a medio
  // llenar obliga a estimar un ángulo; con muescas se cuenta. El paso se deriva
  // de la vuelta, así que el anillo cierra sea cual sea el tamaño.
  const muesca = circumference / MUESCAS;

  return (
    <div className="level__ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          className="level__ring-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={`${muesca * 0.34} ${muesca * 0.66}`}
        />
        <circle
          className="level__ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
        />
      </svg>
      <div
        className="level__value"
        style={{ fontSize: size < 72 ? 'var(--fs-title-1)' : undefined }}
      >
        {value}
      </div>
      {label ? <span className="sr-only">{label}</span> : null}
    </div>
  );
}

export function Bar({
  progress,
  color,
  label,
}: {
  progress: number;
  color?: string | undefined;
  label?: string;
}): ReactElement {
  return (
    <div
      className="bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      {...(label ? { 'aria-label': label } : {})}
    >
      <div
        className="bar__fill"
        style={{
          width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
          ...(color ? ({ '--area': `var(${color})` } as Record<string, string>) : {}),
        }}
      />
    </div>
  );
}

/**
 * El sello de liga: círculo monocromo que **se llena de abajo arriba**, como un
 * nivel de agua. Es deliberadamente otra familia que las divisas de tramo
 * —chevrones angulares en acento—: forma distinta, color distinto y sitio
 * distinto, para que en la misma tarjeta no se confundan.
 */
export function Sello({
  fill,
  n,
  size = 44,
  empty = false,
  label,
}: {
  fill: number;
  n?: number | string;
  size?: number;
  empty?: boolean;
  label?: string;
}): ReactElement {
  return (
    <div
      className={`sello${empty ? ' sello--vacio' : ''}`}
      style={{ width: size, height: size }}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      {!empty ? (
        <div
          className="sello__agua"
          style={{ height: `${Math.max(0, Math.min(1, fill)) * 100}%` }}
        />
      ) : null}
      {n !== undefined ? (
        <span className="sello__n" style={{ fontSize: size < 32 ? 11 : undefined }}>
          {n}
        </span>
      ) : null}
    </div>
  );
}

/** Hoja inferior. Se cierra con Escape y con el velo; atrapa el foco dentro. */
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="sheet__grab" />
        <header className="section-head">
          <h2 className="t-title-2">{title}</h2>
          <button type="button" className="btn btn--bare" onClick={onClose}>
            Cerrar
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="empty">
      <p className="t-title-3" style={{ color: 'var(--text-1)' }}>
        {title}
      </p>
      {children ? (
        <p className="t-sm" style={{ marginTop: 'var(--sp-2)' }}>
          {children}
        </p>
      ) : null}
      {action ? <div style={{ marginTop: 'var(--sp-4)' }}>{action}</div> : null}
    </div>
  );
}

/**
 * El segmentado, con la pieza que **se desliza**.
 *
 * Antes la pieza acuñada aparecía debajo de la opción nueva y desaparecía de la
 * vieja: dos sucesos donde hay uno solo, y el ojo tiene que deducir que son la
 * misma pieza. Deslizándola no hay nada que deducir, y es el mismo mecanismo
 * —una propiedad personalizada con el índice, cero medidas— que mueve la pieza
 * de la barra de navegación.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  block = false,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  block?: boolean;
}): ReactElement {
  // Si el valor no está en la lista la pieza se queda en la primera casilla, que
  // es lo mismo que hacía antes el `aria-pressed` de todas en falso.
  const activo = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div
      className={`segmented${block ? ' segmented--block' : ''}`}
      role="group"
      aria-label={label}
      style={{ ['--i']: activo, ['--n']: options.length } as CSSProperties}
    >
      <span className="segmented__coin" aria-hidden="true" />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Formatea un número con separador de miles en español. */
export function num(value: number): string {
  return new Intl.NumberFormat('es-ES').format(Math.round(value));
}

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** `2026-09-12` → `12 de septiembre`. Sin año si es el mismo. */
export function fechaLarga(localDate: string, withYear = false): string {
  const [year, month, day] = localDate.split('-').map(Number);
  const name = MESES[(month ?? 1) - 1] ?? '';
  return withYear ? `${day} de ${name} de ${year}` : `${day} de ${name}`;
}

export function fechaCorta(localDate: string): string {
  const [, month, day] = localDate.split('-').map(Number);
  return `${day} ${(MESES[(month ?? 1) - 1] ?? '').slice(0, 3)}`;
}

/**
 * EL SELECTOR.
 *
 * Sustituye al `<select>` nativo, que era el único control de toda la app que no
 * obedecía a `tokens.css` —lo pinta el sistema operativo, con su tipografía, su
 * azul y su sombra— y por tanto el único que no cambiaba con el tema.
 *
 * Lo que se gana no compensa perder el teclado, así que aquí está entero: rol de
 * combobox, `aria-expanded`, `aria-activedescendant`, y flechas, Inicio, Fin,
 * Intro, Escape y Tab. El resalte del teclado y el del ratón son el mismo
 * atributo (`data-cursor`): si la flecha abajo pintase una cosa y el ratón otra,
 * con las dos manos encima habría dos cursores a la vez.
 *
 * Escape llama a `stopPropagation`. El selector se abre a menudo DENTRO de una
 * hoja, y la hoja escucha Escape en `document`: sin cortar la propagación, una
 * sola pulsación cerraría el selector y la hoja, y el usuario perdería el
 * formulario entero por querer cerrar un desplegable.
 */
export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  id,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  id?: string;
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const [cursor, setCursor] = useState(0);
  const gatillo = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  const base = useId();

  const elegido = options.findIndex((option) => option.value === value);
  const actual = options[elegido >= 0 ? elegido : 0];

  const cerrar = (): void => {
    setAbierto(false);
    gatillo.current?.focus();
  };

  const elegir = (indice: number): void => {
    const option = options[indice];
    if (option) onChange(option.value);
    cerrar();
  };

  useEffect(() => {
    if (!abierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lista.current?.focus();
    return () => {
      document.body.style.overflow = previo;
    };
  }, [abierto]);

  // La opción resaltada tiene que estar a la vista. Con las 25 horas del día,
  // sin esto la flecha abajo mueve un resalte que no se ve.
  useEffect(() => {
    if (!abierto) return;
    document.getElementById(`${base}-${cursor}`)?.scrollIntoView({ block: 'nearest' });
  }, [abierto, cursor, base]);

  const teclas = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const n = options.length;
    if (n === 0) return;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        cerrar();
        return;
      case 'ArrowDown':
        event.preventDefault();
        setCursor((c) => (c + 1) % n);
        return;
      case 'ArrowUp':
        event.preventDefault();
        setCursor((c) => (c - 1 + n) % n);
        return;
      case 'Home':
        event.preventDefault();
        setCursor(0);
        return;
      case 'End':
        event.preventDefault();
        setCursor(n - 1);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        elegir(cursor);
        return;
      case 'Tab':
        event.preventDefault();
        cerrar();
        return;
      default:
    }
  };

  return (
    <>
      <button
        ref={gatillo}
        type="button"
        className="selector__gatillo"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={`${base}-lista`}
        {...(id ? { id } : {})}
        onClick={() => {
          setCursor(elegido >= 0 ? elegido : 0);
          setAbierto(true);
        }}
      >
        <span className="grow">{actual?.label ?? ''}</span>
        <IconDespliega />
      </button>

      {abierto ? (
        <div
          className="scrim"
          onClick={(event) => {
            if (event.target === event.currentTarget) cerrar();
          }}
        >
          <div className="panel">
            <p className="panel__titulo t-caption t-dim">{label}</p>
            <div
              ref={lista}
              id={`${base}-lista`}
              role="listbox"
              tabIndex={-1}
              aria-label={label}
              aria-activedescendant={`${base}-${cursor}`}
              onKeyDown={teclas}
            >
              {options.map((option, i) => (
                <div
                  key={option.value}
                  id={`${base}-${i}`}
                  role="option"
                  aria-selected={option.value === value}
                  data-cursor={i === cursor}
                  className="panel__opcion"
                  onMouseMove={() => setCursor(i)}
                  onClick={() => elegir(i)}
                >
                  <span className="grow">{option.label}</span>
                  {option.value === value ? <IconCheck size={16} /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS DOS GESTOS QUE NO SE DISPARAN AL ABRIR LA APP.
 *
 * Una animación de celebración escrita en CSS se ejecuta cuando el elemento
 * aparece, y eso significa que entrar en Hoy con cinco hábitos ya marcados
 * dispara cinco celebraciones de algo que hiciste ayer. Es la misma familia de
 * fallo que el calendario con veintisiete «0/1» de antes de que el hábito
 * existiera, y que el rewind diciendo «paró en octubre» en septiembre: **la app
 * afirmando algo sobre el usuario a partir de un hueco en sus propios datos**,
 * solo que al revés —aquí felicitándole por nada—.
 *
 * Los dos ganchos comparten el mismo mecanismo: un estado que arranca con el
 * valor actual, así que en el montaje no hay cambio que detectar y no se
 * programa nada. Solo hay gesto cuando el valor cambia **estando tú delante**.
 *
 * Y el cambio se detecta DURANTE EL RENDER, no en un efecto. Es el patrón que
 * React documenta para ajustar estado cuando cambia una entrada, y aquí además
 * lo exige el compilador: `setState` síncrono dentro de un efecto provoca un
 * render en cascada —se pinta el estado viejo y acto seguido el nuevo—, y en una
 * lista de hábitos eso es un fotograma con el círculo a medio rellenar. Lo que
 * sí vive en un efecto es apagar el gesto, porque eso es un temporizador: un
 * sistema externo del que React se suscribe.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** `true` durante `ms` a partir del momento en que `activo` pasa a serlo. */
export function useReciente(activo: boolean, ms = 700): boolean {
  const [previo, setPrevio] = useState(activo);
  const [reciente, setReciente] = useState(false);

  if (activo !== previo) {
    setPrevio(activo);
    setReciente(activo);
  }

  useEffect(() => {
    if (!reciente) return;
    const t = setTimeout(() => setReciente(false), ms);
    return () => clearTimeout(t);
  }, [reciente, ms]);

  return reciente;
}

/**
 * Cuánto acaba de subir `valor`, o `null`. Las bajadas no producen gesto.
 *
 * `valor` admite `null`, y eso **no** es una comodidad: significa «todavía no se
 * sabe». Sin ese estado, la primera versión de esto felicitaba con un «+93» al
 * abrir la app, porque los puntos del día arrancan en 0 mientras IndexedDB
 * responde y suben a 93 cuando llega la respuesta. Para el gancho eso era una
 * subida de 93 puntos; para el usuario era la app celebrando que había
 * terminado de cargar.
 *
 * Es la misma familia de fallo que el calendario con veintisiete «0/1» de antes
 * de que el hábito existiera y que el rewind diciendo «paró en octubre» en
 * septiembre —la app afirmando algo a partir de un hueco en sus propios datos—,
 * solo que esta vez a favor del usuario, que no la hace menos falsa.
 */
export function useSubida(valor: number | null, ms = 1100): number | null {
  const [previo, setPrevio] = useState(valor);
  const [delta, setDelta] = useState<number | null>(null);

  if (valor !== previo) {
    setPrevio(valor);
    setDelta(previo !== null && valor !== null && valor > previo ? valor - previo : null);
  }

  useEffect(() => {
    if (delta === null) return;
    const t = setTimeout(() => setDelta(null), ms);
    return () => clearTimeout(t);
  }, [delta, ms]);

  return delta;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA INSIGNIA DE NIVEL.
 *
 * Era un anillo de 8 px con un número dentro, y se veía por lo que era: un
 * indicador de progreso al que le habían puesto una cifra en medio. En la
 * esquina de la pantalla que se abre todos los días, y siendo la progresión la
 * espina dorsal del producto, eso es poco.
 *
 * Ahora es una pieza con material, y el material es el que ya estaba definido:
 * un **surco** hundido (§14: «lo que se rellena vive en un surco») con el
 * arco dentro, y encima una **pieza acuñada** convexa con el número. Medalla,
 * no gráfico.
 *
 * Y el anillo va **partido en tantos tramos como áreas tengas**. Esto no es
 * decoración: el nivel general es ⌊media de los niveles de área⌋, así que la
 * parte decimal de esa media es siempre un múltiplo exacto de 1/N. Con cuatro
 * áreas, el anillo solo puede estar a 0, ¼, ½ o ¾ — y partido en cuatro, la
 * insignia dice de un vistazo «tres de mis cuatro áreas ya han pasado al
 * siguiente», que es exactamente lo que significa el número. Un anillo continuo
 * en esa misma situación insinúa una precisión que no existe.
 *
 * Por encima de ocho áreas los tramos se hacen ilegibles a 44 px y vuelve a ser
 * continuo. Es el único caso en que la insignia redondea.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * EL DIAL, la cabecera de Progreso.
 *
 * A 44 px en la cabecera de Hoy, lo de arriba es exactamente lo que hace falta.
 * A 112 px, siendo el héroe de la pantalla de progresión, no: un anillo gris
 * con un número es el motivo entero de que el cliente dijera que la piel
 * expresiva se veía sobria. Ahí la insignia pasa a **instrumento**, y el cambio
 * no es de adorno sino de información:
 *
 *   ARCOS INTERIORES  uno por área, cada uno **en su tinte y relleno a SU
 *                     propio progreso**. El anillo deja de decir «vas por ¾» y
 *                     pasa a decir «Cuerpo va lanzado, Orden está parado», que
 *                     es lo que uno viene a mirar.
 *   ESCALA EXTERIOR   marcas finas alrededor, en ámbar hasta donde llega la
 *                     media general. Recoge el dato que los arcos sueltan al
 *                     repartirse entre áreas, así que no se pierde nada.
 *
 * Las dos son opcionales y por separado (`arcos`, `escala`). Sin ellas la
 * insignia es la de siempre, bit a bit: Hoy no se entera de que esto existe.
 * ───────────────────────────────────────────────────────────────────────────
 * ═══════════════════════════════════════════════════════════════════════════
 */
const MAX_TRAMOS_LEGIBLES = 8;

/** Marcas de la escala exterior. 48 = múltiplo de 2, 3, 4, 6 y 8: sea cual sea
 *  el número de áreas, los cuartos de la escala caen sobre un hueco de arco. */
const MARCAS_ESCALA = 48;

/**
 * La escala del dial. Marcas rectas alrededor del anillo, encendidas en ámbar
 * hasta donde llega la media general.
 *
 * Las marcas son `<line>` y no un `stroke-dasharray` sobre un círculo porque un
 * dasharray no se puede truncar: se repite entero o no está, y aquí hace falta
 * que las primeras N estén encendidas y el resto apagadas. Son 48 nodos en una
 * sola instancia de una sola pantalla; el coste es ninguno y el control, exacto.
 *
 * El lienzo ya viene girado −90° desde `.insignia svg`, así que el ángulo 0 de
 * aquí —las 3 en punto— cae arriba, que es por donde se empieza a contar.
 */
function Escala({ size, banda, p }: { size: number; banda: number; p: number }): ReactElement {
  const fuera = size / 2 - 1;
  const encendidas = Math.round(p * MARCAS_ESCALA);

  return (
    <g className="insignia__escala">
      {Array.from({ length: MARCAS_ESCALA }, (_, i) => {
        const angulo = (i / MARCAS_ESCALA) * 2 * Math.PI;
        // Cada doceava marca es un cuarto de vuelta: más larga, para que la
        // escala se lea como escala y no como una textura punteada.
        const largo = banda * (i % 12 === 0 ? 0.82 : 0.44);
        const cos = Math.cos(angulo);
        const sen = Math.sin(angulo);
        return (
          <line
            key={i}
            className="insignia__marca"
            data-lleno={i < encendidas}
            x1={size / 2 + cos * fuera}
            y1={size / 2 + sen * fuera}
            x2={size / 2 + cos * (fuera - largo)}
            y2={size / 2 + sen * (fuera - largo)}
          />
        );
      })}
    </g>
  );
}

export type ArcoInsignia = {
  /** Nombre del token de color del área, p. ej. `--dim-3`. */
  token: string;
  /** 0 a 1. Lo que esa área lleva andado hacia su siguiente nivel. */
  progress: number;
};

export function Insignia({
  nivel,
  progress,
  segmentos = 0,
  arcos,
  escala = false,
  size = 44,
  label,
}: {
  nivel: number | string;
  /** 0 a 1. La parte decimal de la media de niveles de área. */
  progress: number;
  /** Cuántas áreas hay. 0 o más de ocho ⇒ anillo continuo. */
  segmentos?: number;
  /** Un arco por área, cada uno con su tinte y su propio relleno. Manda sobre
   *  `segmentos`: si viene esto, el anillo es el dial. */
  arcos?: readonly ArcoInsignia[];
  /** Dibuja la escala de marcas exterior con la media general en ámbar. */
  escala?: boolean;
  size?: number;
  label?: string;
}): ReactElement {
  // La escala se come una banda del radio. Reservarla ANTES de calcular el
  // radio del anillo es lo que evita que las marcas se salgan del viewBox.
  const banda = escala ? size * 0.11 : 0;
  const grosor = Math.max(3, size * 0.08);
  const hueco = Math.max(2.2, size * 0.055);
  const radio = size / 2 - banda - grosor / 2 - 1;
  const vuelta = 2 * Math.PI * radio;
  const disco = size - 2 * (banda + grosor + hueco);

  const p = Math.max(0, Math.min(1, progress));
  const dial = arcos !== undefined && arcos.length > 0 && arcos.length <= MAX_TRAMOS_LEGIBLES;
  const partido = !dial && segmentos > 0 && segmentos <= MAX_TRAMOS_LEGIBLES;
  const llenos = partido ? Math.round(p * segmentos) : 0;

  return (
    <div
      className={`insignia${escala ? ' insignia--dial' : ''}`}
      style={{ width: size, height: size }}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {escala ? <Escala size={size} banda={banda} p={p} /> : null}
        {dial ? (
          arcos.map((arco, i) => {
            const paso = vuelta / arcos.length;
            const largo = Math.max(1, paso - hueco * 1.6);
            const offset = -(i * paso + (paso - largo) / 2);
            const lleno = largo * Math.max(0, Math.min(1, arco.progress));
            return (
              <Fragment key={i}>
                {/* El surco del área. Va EN SU TINTE, apagado, y no en el gris
                    neutro de la insignia de 44. El motivo es el arranque: con
                    todo a cero —que es como se abre una cuenta nueva— un surco
                    neutro deja el dial exactamente igual de gris que el anillo
                    al que sustituye, y era justo el fallo que venía a arreglar.
                    Tintado, las áreas se distinguen desde el primer día y cada
                    arco se llena en su propio color en vez de aparecer de la
                    nada. Se ve vacío igual: la mitad del dato es el hueco. */}
                <circle
                  className="insignia__arco insignia__arco--surco"
                  data-lleno={false}
                  cx={size / 2}
                  cy={size / 2}
                  r={radio}
                  strokeWidth={grosor}
                  strokeDasharray={`${largo} ${vuelta - largo}`}
                  strokeDashoffset={offset}
                  style={{ stroke: `var(${arco.token})` }}
                />
                {lleno > 0 ? (
                  <circle
                    className="insignia__arco"
                    data-lleno={true}
                    cx={size / 2}
                    cy={size / 2}
                    r={radio}
                    strokeWidth={grosor}
                    strokeDasharray={`${lleno} ${vuelta - lleno}`}
                    strokeDashoffset={offset}
                    style={{ stroke: `var(${arco.token})` }}
                  />
                ) : null}
              </Fragment>
            );
          })
        ) : partido ? (
          Array.from({ length: segmentos }, (_, i) => {
            const paso = vuelta / segmentos;
            const largo = Math.max(1, paso - hueco * 1.6);
            return (
              <circle
                key={i}
                className="insignia__arco"
                data-lleno={i < llenos}
                cx={size / 2}
                cy={size / 2}
                r={radio}
                strokeWidth={grosor}
                strokeDasharray={`${largo} ${vuelta - largo}`}
                strokeDashoffset={-(i * paso + (paso - largo) / 2)}
              />
            );
          })
        ) : (
          <>
            <circle
              className="insignia__arco"
              data-lleno={false}
              cx={size / 2}
              cy={size / 2}
              r={radio}
              strokeWidth={grosor}
            />
            {p > 0 ? (
              <circle
                className="insignia__arco"
                data-lleno={true}
                cx={size / 2}
                cy={size / 2}
                r={radio}
                strokeWidth={grosor}
                strokeDasharray={vuelta}
                strokeDashoffset={vuelta * (1 - p)}
              />
            ) : null}
          </>
        )}
      </svg>
      <span
        className="insignia__disco"
        style={{ width: disco, height: disco, fontSize: Math.round(disco * 0.52) }}
      >
        {nivel}
      </span>
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL DESLIZADOR DE CINCO PASOS.
 *
 * Un `<input type="range">` de verdad, invisible y encima de todo, con la pieza
 * dibujada debajo. Es el único camino que tiene a la vez el aspecto que toca y
 * el comportamiento entero gratis: arrastrar, tocar en cualquier punto del
 * surco para saltar ahí, flechas, Inicio y Fin, y un lector de pantalla que
 * anuncia «bueno, 4 de 5» en vez de leer cinco botones.
 *
 * Dos detalles que no son evidentes:
 *
 *  · **Mientras no lo toques no hay relleno.** El pulgar tiene que estar en
 *    algún sitio, y ponerlo en el centro con la barra llena diría «normal»
 *    antes de que hayas dicho nada. Apagado y con el rótulo en «sin decir», la
 *    pieza dice que está esperando.
 *  · **El corte del relleno cae debajo del pulgar.** Se recorta con `clip-path`
 *    —que se compone y no relayouta, a diferencia de animar el ancho— y el
 *    borde recto que deja queda tapado por la moneda de 26 px. El pulgar se
 *    mueve con `translateX` sobre unidades de contenedor, así que tampoco hay
 *    una sola propiedad de layout animándose.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function Deslizador({
  label,
  labels,
  value,
  onChange,
}: {
  label: string;
  labels: readonly string[];
  value: number | null;
  onChange: (value: number) => void;
}): ReactElement {
  const max = labels.length;
  const actual = value ?? Math.ceil(max / 2);
  const p = max > 1 ? (actual - 1) / (max - 1) : 0;
  const palabra = value === null ? 'sin decir' : (labels[actual - 1] ?? '');

  return (
    <div
      className="deslizador"
      data-tocado={value !== null}
      style={{ ['--p']: p } as CSSProperties}
    >
      <div className="row row--between">
        <span className="t-caption t-dim">{label}</span>
        <span className="deslizador__valor t-sm">{palabra}</span>
      </div>

      <div className="deslizador__pista">
        <span className="deslizador__surco" />
        <span className="deslizador__lleno" />
        {labels.map((texto, i) => (
          <span
            key={texto}
            className="deslizador__marca"
            data-lleno={value !== null && i + 1 <= actual}
            style={{ ['--f']: max > 1 ? i / (max - 1) : 0 } as CSSProperties}
          />
        ))}
        <input
          type="range"
          min={1}
          max={max}
          step={1}
          value={actual}
          aria-label={label}
          aria-valuetext={palabra}
          onChange={(event) => onChange(Number(event.target.value))}
          // Tocar justo en el paso donde ya está el pulgar no cambia el valor y
          // por tanto no dispara `change`. Sin esto, elegir «normal» —que es el
          // sitio donde el pulgar espera— no registraría nada.
          onPointerUp={(event) => onChange(Number(event.currentTarget.value))}
        />
        <span className="deslizador__pulgar" />
      </div>
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL CONTADOR.
 *
 * Sube hasta el valor en vez de aparecer puesto. Es el gesto que más «caro»
 * aporta por línea escrita, y aquí además significa algo: la cifra del día
 * recorre la distancia que has recorrido tú.
 *
 * Sin librería de animación. `framer-motion` son ~34 kB gzip —el presupuesto
 * entero del arranque es 180 y vamos por 175— y esto son doce líneas de
 * `requestAnimationFrame`. La curva es una cúbica de salida: rápido al
 * principio, se posa al final; la misma forma que `--ease-emphasized`.
 *
 * `setState` vive dentro del callback de rAF, no en el cuerpo del efecto: eso
 * es suscribirse a un sistema externo, que es justamente para lo que los
 * efectos existen.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function useContador(valor: number, ms = 1100): number {
  const [mostrado, setMostrado] = useState(0);
  const desde = useRef(0);

  useEffect(() => {
    const origen = desde.current;
    if (origen === valor) return;

    // Con movimiento reducido no se elimina la información, se quita el viaje.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      desde.current = valor;
      setMostrado(valor);
      return;
    }

    const inicio = performance.now();
    let id = requestAnimationFrame(function paso(ahora: number) {
      const t = Math.min(1, (ahora - inicio) / ms);
      const e = 1 - Math.pow(1 - t, 3);
      setMostrado(Math.round(origen + (valor - origen) * e));
      if (t < 1) id = requestAnimationFrame(paso);
      else desde.current = valor;
    });
    return () => cancelAnimationFrame(id);
  }, [valor, ms]);

  return mostrado;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA MÉTRICA RADIAL.
 *
 * Un anillo de veinticinco muescas que se rellena. La pista punteada no es un
 * adorno: un anillo liso a medio llenar no dice cuánto falta —hay que estimar
 * un ángulo—, y con muescas se cuenta. Es la misma idea que la insignia partida
 * en áreas, aplicada a una cantidad continua.
 *
 * Veinticinco y no «8 y 12 píxeles de guion» como el original: con un patrón en
 * píxeles la circunferencia no es múltiplo del paso y el último guion se corta a
 * la mitad, justo arriba, donde el ojo empieza a leer. Aquí el paso se deriva de
 * la vuelta, así que el anillo SIEMPRE cierra.
 *
 * Se rellena al entrar con un `@keyframes` que solo declara el `from`: el `to`
 * implícito es el valor calculado del elemento, así que la misma regla sirve
 * para la entrada Y para cada cambio posterior —que van por `transition`— sin
 * que una pise a la otra. Con `animation-fill-mode: forwards` el relleno se
 * quedaría congelado en el valor del montaje y marcar un hábito no movería
 * nada.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function Metrica({
  titulo,
  valor,
  total,
  estado,
  progress,
  pie,
}: {
  titulo: string;
  valor: number;
  total?: number;
  estado: string;
  progress: number;
  pie?: ReactNode;
}): ReactElement {
  const contado = useContador(valor);
  const p = Math.max(0, Math.min(1, progress));
  const paso = VUELTA / MUESCAS;

  return (
    <section className="metrica">
      <p className="t-caption t-dim">{titulo}</p>

      <div
        className="metrica__anillo"
        style={{ ['--vuelta']: VUELTA, ['--corte']: VUELTA * (1 - p) } as CSSProperties}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(p * 100)}
        aria-label={`${titulo}: ${estado}`}
      >
        <svg viewBox="0 0 200 200" aria-hidden="true">
          <circle
            className="metrica__pista"
            cx="100"
            cy="100"
            r={RADIO}
            strokeDasharray={`${paso * 0.34} ${paso * 0.66}`}
          />
          <circle className="metrica__relleno" cx="100" cy="100" r={RADIO} />
        </svg>

        <div className="metrica__centro">
          <p className="metrica__valor">
            <span className="tnum">{contado}</span>
            {total !== undefined ? <span className="metrica__de">/{total}</span> : null}
          </p>
          <p className="metrica__estado">{estado}</p>
        </div>
      </div>

      {pie ? <div className="metrica__pie">{pie}</div> : null}
    </section>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL MENÚ DE UNA FILA.
 *
 * Existe porque faltaba: se podía crear una meta y no había forma de
 * renombrarla, de cambiarle el plazo ni de retirarla. Una app en la que lo que
 * escribes es definitivo no es una app de metas, es un formulario.
 *
 * Comparte el panel con el selector —mismo sitio, mismo tamaño, mismas teclas—
 * y esa es la razón de que las clases se llamen `panel` y no `selector`: una
 * lista de opciones que se abre desde un control se ve igual sea para elegir un
 * valor o para elegir una acción, y aprender dos veces lo mismo es aprender de
 * más.
 *
 * La acción destructiva va marcada y va la última. Nunca es la primera de la
 * lista, que es donde cae el dedo cuando el panel aparece bajo él.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function Menu({
  label,
  acciones,
}: {
  label: string;
  acciones: readonly { texto: string; onSelect: () => void; peligro?: boolean }[];
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const gatillo = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLDivElement>(null);

  const cerrar = (): void => {
    setAbierto(false);
    gatillo.current?.focus();
  };

  useEffect(() => {
    if (!abierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lista.current?.focus();
    return () => {
      document.body.style.overflow = previo;
    };
  }, [abierto]);

  return (
    <>
      <button
        ref={gatillo}
        type="button"
        className="btn btn--bare btn--icon"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={`Opciones de ${label}`}
        onClick={() => setAbierto(true)}
      >
        <IconOpciones />
      </button>

      {/* En `body`: dentro de un diálogo de cristal (`contain: paint`) un `fixed`
          quedaría recortado a la caja del diálogo. */}
      {abierto
        ? createPortal(
            <div
              className="scrim"
              onClick={(event) => {
                if (event.target === event.currentTarget) cerrar();
              }}
            >
              <div className="panel">
                <p className="panel__titulo t-caption t-dim">{label}</p>
                <div
                  ref={lista}
                  role="menu"
                  tabIndex={-1}
                  aria-label={label}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return;
                    event.preventDefault();
                    // Cortar aquí: el menú se abre dentro de listas que viven en
                    // hojas, y sin esto una sola pulsación cerraría las dos.
                    event.stopPropagation();
                    cerrar();
                  }}
                >
                  {acciones.map((accion) => (
                    <button
                      key={accion.texto}
                      type="button"
                      role="menuitem"
                      className="panel__opcion"
                      data-peligro={accion.peligro === true}
                      onClick={() => {
                        cerrar();
                        accion.onSelect();
                      }}
                    >
                      {accion.texto}
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * EL DIÁLOGO CENTRADO. La hoja inferior sirve para elegir; esto es para un momento
 * propio, como el ritual. Se pinta en `body` con un portal para poder dejar inerte
 * todo `#root` —lector de pantalla y tabulador incluidos— y devuelve el foco a quien
 * lo abrió al cerrarse.
 */
export function Dialogo({
  open,
  onClose,
  titulo,
  pie,
  ancho = 'normal',
  ajustado = false,
  focoAlCerrar,
  children,
}: {
  open: boolean;
  onClose: () => void;
  titulo: string;
  pie?: ReactNode;
  /** `amplio` deja sitio a dos columnas, como el riel del ritual junto a la pregunta. */
  ancho?: 'normal' | 'amplio';
  /** En móvil, del alto de su contenido y no de pantalla entera: para algo breve. */
  ajustado?: boolean;
  /**
   * Adónde va el foco al cerrar si quien abrió ya no existe: sellar el ritual quita
   * el botón «Empezar…» y, sin esto, el foco caía en `body`.
   */
  focoAlCerrar?: RefObject<HTMLElement | null> | undefined;
  children: ReactNode;
}): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);
  const tituloId = useId();
  const cerrar = useRef(onClose);
  const respaldo = useRef(focoAlCerrar);
  useEffect(() => {
    cerrar.current = onClose;
    respaldo.current = focoAlCerrar;
  });

  useEffect(() => {
    if (!open) return;
    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const raiz = document.getElementById('root');
    raiz?.setAttribute('inert', '');
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        cerrar.current();
        return;
      }
      if (event.key !== 'Tab' || !ref.current) return;
      const focos = ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      if (!primero || !ultimo) {
        event.preventDefault();
        return;
      }
      // El contenedor, que es donde empieza el foco, cuenta como parte del ciclo:
      // sin él, Shift+Tab desde ahí se escapaba del diálogo.
      const enElBorde =
        document.activeElement === primero || document.activeElement === ref.current;
      if (event.shiftKey && enElBorde) {
        event.preventDefault();
        ultimo.focus();
      } else if (!event.shiftKey && document.activeElement === ultimo) {
        event.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      raiz?.removeAttribute('inert');
      document.body.style.overflow = overflow;
      if (previo?.isConnected) previo.focus();
      else respaldo.current?.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="scrim scrim--dialogo"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`dialogo glass-3${ancho === 'amplio' ? ' dialogo--amplio' : ''}${ajustado ? ' dialogo--ajustado' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        tabIndex={-1}
        ref={ref}
      >
        <header className="dialogo__cabecera">
          <h2 id={tituloId} className="t-title-2">
            {titulo}
          </h2>
          <button
            type="button"
            className="btn btn--bare btn--icon"
            aria-label="Cerrar"
            onClick={onClose}
          >
            <IconCerrar />
          </button>
        </header>
        <div className="dialogo__cuerpo">{children}</div>
        {pie ? <footer className="dialogo__pie">{pie}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

/** Indicador de pasos: el surco hundido que se rellena acuñado, como los retos. */
export function Pasos({
  total,
  actual,
  etiqueta,
}: {
  total: number;
  actual: number;
  etiqueta: string;
}): ReactElement {
  return (
    <ol className="pasos" aria-label={etiqueta}>
      {Array.from({ length: total }, (_, i) => (
        <li
          key={i}
          className="pasos__surco"
          data-estado={i < actual ? 'hecho' : i === actual ? 'actual' : 'pendiente'}
          aria-current={i === actual ? 'step' : undefined}
        >
          <span className="sr-only">
            Paso {i + 1} de {total}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Una tarea. El círculo es EL MISMO que el de marcar un hábito —mismo tamaño,
 * misma respuesta al toque, mismo cuajo al hacerse—: una app con dos gestos
 * distintos para «hecho» enseña dos cosas donde hay una.
 */
export function FilaTarea({
  titulo,
  hecha,
  dificil = false,
  nota,
  color,
  onToggle,
  acciones,
}: {
  titulo: string;
  hecha: boolean;
  dificil?: boolean;
  nota?: string;
  color?: string | undefined;
  onToggle: () => void;
  acciones?: readonly { texto: string; onSelect: () => void; peligro?: boolean }[];
}): ReactElement {
  const reciente = useReciente(hecha);
  return (
    <li
      className={`tarea${hecha ? ' tarea--hecha' : ''}${dificil ? ' tarea--dificil' : ''}`}
      style={color ? { ['--area' as string]: `var(${color})` } : undefined}
    >
      <button
        type="button"
        className="habit__check"
        aria-pressed={hecha}
        aria-label={hecha ? `Desmarcar ${titulo}` : `Marcar ${titulo} como hecha`}
        data-reciente={reciente}
        onClick={onToggle}
      >
        <span>{hecha ? <IconCheck size={16} /> : null}</span>
      </button>
      <span className="grow tarea__texto">
        <span className="tarea__titulo">{titulo}</span>
        {dificil ? (
          <span className="tarea__marca t-caption">
            <span aria-hidden="true">Difícil</span>
            <span className="sr-only">Lo difícil primero</span>
          </span>
        ) : null}
        {nota ? <span className="t-sm t-dim">{nota}</span> : null}
      </span>
      {acciones && acciones.length > 0 ? <Menu label={titulo} acciones={acciones} /> : null}
    </li>
  );
}

/** Tira de días desplazable. El elegido se centra solo; un punto marca los que tienen algo. */
export function TiraDias({
  dias,
  elegido,
  hoy,
  conMarca,
  onElegir,
}: {
  dias: readonly string[];
  elegido: string;
  hoy: string;
  conMarca?: ReadonlySet<string>;
  onElegir: (dia: string) => void;
}): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current
      ?.querySelector('[aria-pressed="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [elegido]);

  return (
    <div className="tira" ref={ref} role="group" aria-label="Días">
      {dias.map((dia) => (
        <button
          key={dia}
          type="button"
          className="tira__dia"
          aria-pressed={dia === elegido}
          aria-label={etiquetaDelDia(
            fechaLarga(dia, true),
            dia === hoy,
            conMarca?.has(dia) === true,
          )}
          data-hoy={dia === hoy}
          onClick={() => onElegir(dia)}
        >
          <span className="tira__letra t-caption">{letraDelDia(dia)}</span>
          <span className="tira__n">{Number(dia.slice(8))}</span>
          <span
            className={`tira__marca${conMarca?.has(dia) ? '' : ' tira__marca--vacia'}`}
            aria-hidden="true"
          />
        </button>
      ))}
    </div>
  );
}

/**
 * El «+N» que sube y se apaga: la misma pieza que los puntos de Hoy, para cualquier
 * acción que puntúe fuera de esa cifra. Quien la usa le pone `key` para relanzarla.
 */
export function ChispaPuntos({ puntos }: { puntos: number | null }): ReactElement | null {
  if (puntos === null || puntos <= 0) return null;
  return (
    <span className="puntos__delta chispa t-sm" aria-hidden="true">
      +{num(puntos)}
    </span>
  );
}

/**
 * Una escala hecha de segmentos, llenos los que diga `llenos` (ver `segmentos()` en
 * el tablero): la energía en el riel del ritual y en el tablero. Solo se lee; para
 * elegir, el ritual tiene su medidor con botones.
 */
export function Segmentos({
  llenos,
  label,
}: {
  llenos: readonly boolean[];
  label: string;
}): ReactElement {
  return (
    <span className="segmentos" role="img" aria-label={label}>
      {llenos.map((lleno, i) => (
        <span key={i} className="segmentos__uno" data-lleno={lleno} />
      ))}
    </span>
  );
}
